import { useState, useEffect } from 'react'
import { User } from '@supabase/supabase-js'
import { supabase } from '@/integrations/supabase/client'

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [checkingAdmin, setCheckingAdmin] = useState(false)

  useEffect(() => {
    console.log('useAuth: Starting auth initialization')
    
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('useAuth: Got initial session:', { 
        hasUser: !!session?.user, 
        userEmail: session?.user?.email 
      })
      
      setUser(session?.user ?? null)
      
      // Only check admin status if user exists
      if (session?.user) {
        console.log('useAuth: Checking admin status for existing session')
        checkAdminStatusSilent(session.user)
      } else {
        console.log('useAuth: No existing session, setting loading to false')
        setLoading(false)
      }
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('useAuth: Auth state changed:', { 
          event, 
          hasUser: !!session?.user,
          userEmail: session?.user?.email 
        })
        
        setUser(session?.user ?? null)
        
        if (session?.user) {
          console.log('useAuth: User logged in, checking admin status')
          checkAdminStatusSilent(session.user)
        } else {
          console.log('useAuth: User logged out, resetting admin status')
          setIsAdmin(false)
          setLoading(false)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  // Silent admin check for initial load and auth state changes
  const checkAdminStatusSilent = async (user: User) => {
    console.log('useAuth: Checking admin status for user:', user.email)
    
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('id')
        .eq('id', user.id)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          console.log('useAuth: User is not an admin (not found in admin_users)')
          setIsAdmin(false)
        } else {
          console.error('useAuth: Error checking admin status:', error)
          setIsAdmin(false)
        }
        setLoading(false)
        return
      }

      console.log('useAuth: User is an admin!')
      setIsAdmin(!!data)
      setLoading(false)
    } catch (error) {
      console.error('useAuth: Exception checking admin status:', error)
      setIsAdmin(false)
      setLoading(false)
    }
  }

  // Explicit admin check with loading state for login attempts
  const checkAdminStatus = async (user: User | null) => {
    setCheckingAdmin(true)
    
    if (!user) {
      setIsAdmin(false)
      setCheckingAdmin(false)
      return
    }

    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('id')
        .eq('id', user.id)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          setIsAdmin(false)
        } else {
          console.error('Error checking admin status:', error)
          setIsAdmin(false)
        }
        return
      }

      setIsAdmin(!!data)
    } catch (error) {
      console.error('Exception checking admin status:', error)
      setIsAdmin(false)
    } finally {
      setCheckingAdmin(false)
    }
  }

  const signIn = async (email: string, password: string) => {
    console.log('useAuth: Starting sign in for:', email)
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    
    console.log('useAuth: Sign in result:', { 
      hasUser: !!data.user, 
      error: error?.message 
    })
    
    return { data, error }
  }

  const signUp = async (email: string, password: string, displayName?: string, nationality?: string) => {
    console.log('useAuth: Starting sign up for:', email)
    
    const redirectUrl = `${window.location.origin}/`
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          display_name: displayName,
          nationality: nationality
        }
      }
    })
    
    console.log('useAuth: Sign up result:', { 
      hasUser: !!data.user, 
      error: error?.message 
    })
    
    return { data, error }
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  }

  return {
    user,
    loading,
    isAdmin,
    checkingAdmin,
    signIn,
    signUp,
    signOut,
  }
}